import { db } from '../config/firebase'
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore'

// Lista de todos os jogos
const ALL_GAMES = ['tetris', 'snake', 'pong', 'breakout', 'sancode', 'codeinbug']

// Níveis gratuitos por jogo (Tetris = ilimitado)
const FREE_LEVELS = {
  tetris: -1,      // -1 = ilimitado
  snake: 3,        // 3 níveis grátis
  pong: 3,         // 3 níveis grátis
  breakout: 3,     // 3 níveis grátis
  sancode: 3,      // 3 níveis grátis
  codeinbug: 3     // 3 níveis grátis
}

export async function getUserGameProgress(userId) {
  if (!userId) return null
  
  try {
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)
    
    if (!userDoc.exists()) {
      // Cria progresso inicial - Tetris sempre liberado full
      const initialProgress = {
        unlockedGames: ['tetris'],  // Tetris sempre desbloqueado
        hasFullAccess: false,        // Pagamento completo
        payments: [],
        playTime: {
          tetris: 0,
          snake: 0,
          pong: 0,
          breakout: 0,
          sancode: 0,
          codeinbug: 0
        },
        levelsCompleted: {
          tetris: 0,
          snake: 0,
          pong: 0,
          breakout: 0,
          sancode: 0,
          codeinbug: 0
        },
        createdAt: new Date()
      }
      await setDoc(userRef, initialProgress)
      return initialProgress
    }
    
    return userDoc.data()
  } catch (error) {
    console.error('Error getting user progress:', error)
    return null
  }
}

export async function updatePlayTime(userId, game, seconds) {
  if (!userId || !game) return
  
  try {
    const userRef = doc(db, 'users', userId)
    await updateDoc(userRef, {
      [`playTime.${game}`]: increment(seconds)
    })
  } catch (error) {
    console.error('Error updating play time:', error)
  }
}

// Verifica se usuário pode jogar determinado nível de um jogo
export async function canPlayLevel(userId, game, level) {
  if (!userId) return false
  
  const progress = await getUserGameProgress(userId)
  if (!progress) return false
  
  // Se tem acesso full, libera tudo
  if (progress.hasFullAccess) return true
  
  // Tetris sempre liberado (todos os níveis)
  if (game === 'tetris') return true
  
  // Verifica níveis gratuitos
  const freeLimit = FREE_LEVELS[game] || 0
  
  // Se está dentro dos níveis gratuitos
  if (freeLimit === -1 || level <= freeLimit) {
    return true
  }
  
  // Precisa pagar para jogar além dos níveis gratuitos
  return false
}

export async function checkGameUnlock(userId, game) {
  const progress = await getUserGameProgress(userId)
  if (!progress) return false
  
  // Tetris sempre desbloqueado
  if (game === 'tetris') return true
  
  // Se tem acesso full, desbloqueia todos os jogos
  if (progress.hasFullAccess) return true
  
  // Verifica se já está na lista de desbloqueados
  if (progress.unlockedGames && progress.unlockedGames.includes(game)) return true
  
  return false
}

export async function unlockGame(userId, game) {
  if (!userId || !game) return
  
  try {
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)
    
    if (userDoc.exists()) {
      const data = userDoc.data()
      const unlockedGames = data.unlockedGames || ['tetris']
      
      if (!unlockedGames.includes(game)) {
        await updateDoc(userRef, {
          unlockedGames: [...unlockedGames, game]
        })
      }
    }
  } catch (error) {
    console.error('Error unlocking game:', error)
  }
}

export async function registerPayment(userId, paymentData) {
  if (!userId) return
  
  try {
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)
    
    if (userDoc.exists()) {
      const data = userDoc.data()
      const payments = data.payments || []
      const unlockedGames = data.unlockedGames || ['tetris']
      
      // Registra pagamento
      payments.push({
        ...paymentData,
        timestamp: new Date()
      })
      
      let newUnlockedGames = [...unlockedGames]
      let hasFullAccess = data.hasFullAccess || false
      
      // PACOTE COMPLETO - R$ 40,00 ou mais
      if (paymentData.amount >= 40) {
        // Desbloqueia TODOS os jogos (atuais e futuros)
        newUnlockedGames = ALL_GAMES
        hasFullAccess = true
        
        await updateDoc(userRef, {
          payments,
          unlockedGames: newUnlockedGames,
          hasFullAccess: true,
          fullAccessGrantedAt: new Date()
        })
        
        return { unlockedGames: newUnlockedGames, hasFullAccess: true }
      } 
      // JOGO INDIVIDUAL - R$ 7,99
      else if (paymentData.amount >= 7 && paymentData.gameToUnlock) {
        // Desbloqueia apenas o jogo específico
        if (!newUnlockedGames.includes(paymentData.gameToUnlock)) {
          newUnlockedGames.push(paymentData.gameToUnlock)
        }
        
        await updateDoc(userRef, {
          payments,
          unlockedGames: newUnlockedGames
        })
        
        return { unlockedGames: newUnlockedGames, hasFullAccess: false }
      }
      
      // Pagamento não reconhecido
      return { unlockedGames, hasFullAccess }
    }
  } catch (error) {
    console.error('Error registering payment:', error)
    return null
  }
}

export function formatTime(seconds) {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${hours}h ${minutes}m`
}

export function getTimeRemaining(seconds) {
  const remaining = Math.max(0, 7200 - seconds) // 2 horas
  return formatTime(remaining)
}

// Retorna informação sobre acesso ao jogo
export function getGameAccessInfo(game, userProgress) {
  if (!userProgress) {
    return {
      hasAccess: game === 'tetris',
      freeLevels: FREE_LEVELS[game] || 0,
      isFull: game === 'tetris',
      needsPayment: game !== 'tetris'
    }
  }
  
  const hasFullAccess = userProgress.hasFullAccess || false
  const isUnlocked = userProgress.unlockedGames?.includes(game) || false
  
  return {
    hasAccess: hasFullAccess || isUnlocked || game === 'tetris',
    freeLevels: FREE_LEVELS[game] || 0,
    isFull: hasFullAccess || isUnlocked || game === 'tetris',
    needsPayment: !hasFullAccess && !isUnlocked && game !== 'tetris',
    hasFullAccess: hasFullAccess
  }
}
